import React from "react";
import { Phone, Shield, Clock } from "lucide-react";
import { motion } from "framer-motion";
import fireplace1 from "../assets/fireplace1.jpg";
import fireplace2 from "../assets/fireplace2.jpg";
import GalleryAndTestimonials from "../GalleryAndTestimonials";

const Hero = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Balanced animated glow effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/15 via-transparent to-orange-600/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-orange-500/25 rounded-full blur-[130px] glow-pulse" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      <div className="relative isolate px-6 lg:px-8">
        <div className="mx-auto max-w-4xl pt-28 pb-16 sm:py-24 lg:py-28">
          <div className="text-center">
            {/* Trust Badge */}
            <div className="flex justify-center gap-6 mb-6 flex-wrap">
              <div className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-lg px-4 py-2 hover:border-orange-500/50 transition-colors">
                <Shield className="w-5 h-5 text-orange-400" />
                <span className="text-slate-300 text-sm">
                  Licensed & Insured
                </span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-lg px-4 py-2 hover:border-orange-500/50 transition-colors">
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-slate-300 text-sm">
                  Reliable & On-Time
                </span>
              </div>
            </div>

            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
              Professional Gas Services{" "}
              <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                You Can Trust
              </span>
            </h1>

            <p className="mt-4 text-lg text-slate-300 sm:text-xl max-w-2xl mx-auto">
              Expert gas line installation, fireplace services, and maintenance
              for your home. Quality workmanship with attention to safety and
              detail.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:9805406064"
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 text-base font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all w-full sm:w-auto justify-center"
              >
                <Phone className="w-5 h-5" />
                Call (980) 540-6064
              </a>
              {/* <a
                href="request-appointment"
                className="rounded-lg bg-slate-800 border-2 border-slate-600 px-6 py-3 text-base font-semibold text-white hover:bg-slate-700 transition-all w-full sm:w-auto"
              >
              temp comment
              Get Free Quote              
              </a> */}
            </div>

            {/* Service Areas */}
            <p className="mt-6 text-sm text-slate-400">
              Serving Mooresville, Charlotte, Huntersville, and surrounding
              areas
            </p>
          </div>
        </div>

        {/* Fireplace Showcase Section */}
        <div className="max-w-7xl mx-auto px-6 -mt-20">
          {/* First Row */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row items-center gap-8 mb-8"
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="md:w-1/2 space-y-4"
            >
              <h2 className="text-3xl font-bold text-white">
                Luxury Fireplace{" "}
                <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Transform your living space with our premium gas fireplace
                installations. Our modern designs combine aesthetic beauty with
                efficient heating, creating the perfect ambiance for your home.
                From initial consultation to final installation, we deliver
                exceptional craftsmanship and service.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="md:w-1/2"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/25 to-orange-600/15 rounded-lg blur-xl group-hover:blur-2xl transition-all" />
                <img
                  src={fireplace1}
                  alt="Luxury Fireplace"
                  className="relative rounded-lg shadow-2xl w-full h-[400px] object-cover border border-slate-700/50"
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Second Row */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col-reverse md:flex-row items-center gap-8 pb-[50px]"
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="md:w-1/2"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/25 to-orange-600/15 rounded-lg blur-xl group-hover:blur-2xl transition-all" />
                <img
                  src={fireplace2}
                  alt="Custom Fireplace Installation"
                  className="relative rounded-lg shadow-2xl w-full h-[400px] object-cover border border-slate-700/50"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="md:w-1/2 space-y-4"
            >
              <h2 className="text-3xl font-bold text-white">
                Custom Design &{" "}
                <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Installation
                </span>
              </h2>
              <p className="text-slate-300 leading-relaxed">
                From contemporary to classic styles, we offer customized
                fireplace solutions that match your home's architecture and
                personal taste. Our certified technicians ensure safe,
                code-compliant installation with meticulous attention to every
                detail for lasting beauty and performance.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <GalleryAndTestimonials />

      <style>{`
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.35;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Hero;
